import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { novedadService } from '../services/novedadService';
import { TipoNovedad, RegistroNovedad } from '../types/novedad';

export const RegistroNovedadComponent = () => {
  const { user } = useAuth();
  const [tiposNovedad, setTiposNovedad] = useState<TipoNovedad[]>([]);
  const [novedadesActivas, setNovedadesActivas] = useState<RegistroNovedad[]>([]);
  const [selectedTipo, setSelectedTipo] = useState<string>('');
  const [observacion, setObservacion] = useState<string>('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      loadTiposNovedad();
      loadNovedadesActivas();
    }
  }, [user]);

  const loadTiposNovedad = async () => {
    try {
      const tipos = await novedadService.getTiposNovedad();
      setTiposNovedad(tipos);
    } catch (error) {
      console.error('Error al cargar tipos de novedad:', error);
    }
  };

  const loadNovedadesActivas = async () => {
    if (!user) return;
    try {
      const novedades = await novedadService.getNovedadesActivas(user.id);
      setNovedadesActivas(novedades);
    } catch (error) {
      console.error('Error al cargar novedades activas:', error);
    }
  };

  const handleIniciarNovedad = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTipo) return;

    setLoading(true);
    try {
      await novedadService.iniciarNovedad({
        tipo_novedad_id: selectedTipo,
        observacion: observacion.trim() || undefined
      });
      setSelectedTipo('');
      setObservacion('');
      loadNovedadesActivas();
    } catch (error) {
      console.error('Error al iniciar novedad:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFinalizarNovedad = async (novedadId: string) => {
    setLoading(true);
    try {
      await novedadService.finalizarNovedad(novedadId);
      loadNovedadesActivas();
    } catch (error) {
      console.error('Error al finalizar novedad:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4">Registrar Nueva Novedad</h2>
        <form onSubmit={handleIniciarNovedad} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Tipo de Novedad
            </label>
            <select
              value={selectedTipo}
              onChange={(e) => setSelectedTipo(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              required
            >
              <option value="">Seleccione un tipo</option>
              {tiposNovedad.map((tipo) => (
                <option key={tipo.id} value={tipo.id}>
                  {tipo.nombre}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Observación (opcional)
            </label>
            <textarea
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              rows={3}
              maxLength={255}
            />
          </div>

          <button
            type="submit"
            disabled={loading || !selectedTipo}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400"
          >
            {loading ? 'Registrando...' : 'Iniciar Novedad'}
          </button>
        </form>
      </div>

      {novedadesActivas.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Novedades Activas</h2>
          <div className="space-y-4">
            {novedadesActivas.map((novedad) => (
              <div
                key={novedad.id}
                className="border rounded-lg p-4 flex justify-between items-center"
              >
                <div>
                  <h3 className="font-medium">
                    {novedad.tipo_novedad.nombre}
                  </h3>
                  {novedad.observacion && (
                    <p className="text-sm text-gray-500">{novedad.observacion}</p>
                  )}
                  <p className="text-sm text-gray-500">
                    Inicio: {new Date(novedad.fecha_hora_inicio).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={() => handleFinalizarNovedad(novedad.id)}
                  disabled={loading}
                  className="ml-4 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-gray-400"
                >
                  Finalizar
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
